<template>
  <div style="width: 100%;">
    <v-container fluid>
      <v-row>
        <v-col class="d-flex flex-row">
          <h1>Expenses</h1>
          <v-progress-circular
            indeterminate
            color="green"
            v-if="loader"
            style="margin-left: 10px;"
          />
        </v-col>
      </v-row>
      <!-- Dashboard -->
      <v-row class="mb-2">
        <v-col v-for="card in dashboardCards" :key="card.label" cols="12" sm="6" md="4" lg="2">
          <v-card outlined :class="`dashboard-card border-${card.color}`" style="border-top: 4px solid;" :style="`border-top-color: var(--v-${card.color}-base) !important;`" @click="card.filter !== null && filterByStatus(card.filter)">
            <v-card-text class="pa-3">
              <div class="d-flex flex-row justify-space-between align-start">
                <div class="d-flex flex-column">
                  <span class="caption text-uppercase font-weight-bold grey--text">{{ card.label }}</span>
                  <span class="text-h5 font-weight-bold mt-1">{{ card.count }}</span>
                  <span v-if="card.amount !== null" class="caption mt-1">{{ utils.formatCurrency(card.amount) }}</span>
                </div>
                <v-icon :color="card.color" size="32" style="opacity:0.35;">{{ card.icon }}</v-icon>
              </div>
              <div v-if="card.badge" class="mt-2">
                <v-chip x-small :color="card.color" dark>Needs attention</v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="d-flex flex-row align-center">
          <h2>New Expense</h2>
          <v-btn class="mx-2" fab small dark color="info" @click="createA">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
          <v-btn v-if="statusFilter !== null" text small @click="statusFilter = null">
            <v-icon small class="mr-1">mdi-close</v-icon>Clear filter
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-tabs v-model="activeTab" @change="onTabChange">
            <v-tab>All</v-tab>
            <v-tab>One-Time</v-tab>
            <v-tab>Recurring</v-tab>
          </v-tabs>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-card>
            <v-data-table mobile-breakpoint="0"
              :headers="headers"
              :items="displayedExpenses"
              :items-per-page="-1"
              class="elevation-1"
              @click:row="rowClick"
            >
              <template v-slot:item.status="{ item }">
                <v-chip small :color="parseExpenseStatusColor(item.status)">{{ parseExpenseStatus(item.status) }}</v-chip>
              </template>
              <template v-slot:item.isRecurring="{ item }">
                <v-chip small :color="item.isRecurring ? 'primary' : 'grey lighten-1'">
                  {{ item.isRecurring ? 'Recurring' : 'One-Time' }}
                </v-chip>
              </template>
              <template v-slot:item.amount="{ item }">
                <span>{{ utils.formatCurrency(item.amount) }}</span>
              </template>
              <template v-slot:item.date="{ item }">
                <span>{{ utils.formatDate(item.date, 'short') }}</span>
              </template>
              <template v-slot:item.createdBy="{ item }">
                <span>{{ lookupUsername(item.createdBy) }}</span>
              </template>
              <template v-slot:item.createdAt="{ item }">
                <span>{{ utils.formatDate(item.createdAt, 'short') }}</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="createDialog.isOpen" max-width="490">
      <v-card>
        <v-card-title class="text-h5">Create New Expense</v-card-title>
        <v-card-actions>
          <v-btn color="error" text @click="cancelCreate">Cancel</v-btn>
          <v-btn color="success" text @click="createB">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import utils from '../../plugins/helpers';

  export default {
    data() {
      return {
        utils,
        loader: false,
        activeTab: 0,
        statusFilter: null,
        expenses: [],
        recurringExpenses: [],
        singular: 'Expense',
        singularLower: 'expense',
        plural: 'Expenses',
        pluralLower: 'expenses',
        headers: [
          { text: '#', align: 'start', sortable: true, value: 'id' },
          { text: 'Name', align: 'start', sortable: true, value: 'name' },
          { text: 'Paid To', align: 'start', sortable: true, value: 'Supplier.name' },
          { text: 'Category', align: 'start', sortable: true, value: 'ExpenseCategory.name' },
          { text: 'Amount', align: 'start', sortable: true, value: 'amount' },
          { text: 'Type', align: 'start', sortable: true, value: 'isRecurring' },
          { text: 'Date', align: 'start', sortable: true, value: 'date' },
          { text: 'Status', align: 'start', sortable: true, value: 'status' },
          { text: 'Created By', align: 'start', sortable: true, value: 'createdBy' },
          { text: 'Date Created', align: 'start', sortable: true, value: 'createdAt' },
        ],
        createDialog: { isOpen: false },
      };
    },
    computed: {
      ...mapGetters(['getEndpoint', 'lookupUsername']),
      displayedExpenses() {
        let base;
        if (this.activeTab === 2) base = this.recurringExpenses;
        else if (this.activeTab === 1) base = this.expenses.filter(e => !e.isRecurring);
        else base = this.expenses;
        if (this.statusFilter !== null) base = base.filter(e => e.status === this.statusFilter);
        return base;
      },
      dashboardCards() {
        const all = this.expenses;
        const sum = arr => arr.reduce((a, e) => a + parseFloat(e.amount || 0), 0);
        const now = new Date();
        const thisMonth = e => {
          if (!e.paymentDate) return false;
          const d = new Date(e.paymentDate);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        };
        const drafts      = all.filter(e => e.status === 0);
        const pending     = all.filter(e => e.status === 1);
        const approved    = all.filter(e => e.status === 2);
        const rejected    = all.filter(e => e.status === 3);
        const paidMonth   = all.filter(e => e.status === 4 && thisMonth(e));
        const outstanding = all.filter(e => [0,1,2].includes(e.status));
        return [
          { label: 'Outstanding',       count: outstanding.length, amount: sum(outstanding), color: 'blue-grey', icon: 'mdi-currency-usd',       filter: null,  badge: false },
          { label: 'Drafts',            count: drafts.length,      amount: sum(drafts),      color: 'grey',      icon: 'mdi-file-outline',         filter: 0,     badge: false },
          { label: 'Pending Approval',  count: pending.length,     amount: sum(pending),     color: 'info',      icon: 'mdi-clock-outline',        filter: 1,     badge: pending.length > 0 },
          { label: 'Approved',          count: approved.length,    amount: sum(approved),    color: 'primary',   icon: 'mdi-check-circle-outline', filter: 2,     badge: approved.length > 0 },
          { label: 'Rejected',          count: rejected.length,    amount: null,             color: 'warning',   icon: 'mdi-alert-circle-outline', filter: 3,     badge: rejected.length > 0 },
          { label: 'Paid This Month',   count: paidMonth.length,   amount: sum(paidMonth),   color: 'success',   icon: 'mdi-check-all',            filter: 4,     badge: false },
        ];
      },
    },
    async mounted() {
      await this.loadExpenses();
    },
    methods: {
      async loadExpenses() {
        try {
          this.loader = true;
          let res = await axios.get(`${this.getEndpoint}/api/expenses`);
          this.expenses = res.data.data;
        } finally {
          this.loader = false;
        }
      },
      async onTabChange(tab) {
        if (tab === 2 && this.recurringExpenses.length === 0) {
          try {
            this.loader = true;
            let res = await axios.get(`${this.getEndpoint}/api/expenses/recurring`);
            this.recurringExpenses = res.data.data;
          } finally {
            this.loader = false;
          }
        }
      },
      rowClick(row) {
        this.$router.push({ path: `/expenses/view/${row.id}` });
      },
      createA() {
        this.createDialog.isOpen = true;
      },
      async createB() {
        try {
          let res = await axios.post(`${this.getEndpoint}/api/expenses`);
          this.createDialog.isOpen = false;
          await this.$router.push({ path: `/expenses/view/${res.data.data.id}` });
        } catch (error) {
          console.error(error);
          this.$snack(error.msg || error.message || error, 'error');
          this.createDialog.isOpen = false;
        }
      },
      cancelCreate() {
        this.createDialog.isOpen = false;
      },
      filterByStatus(status) {
        this.statusFilter = this.statusFilter === status ? null : status;
      },
      parseExpenseStatus(status) {
        const map = { '-1': 'Voided', 0: 'Draft', 1: 'Pending Approval', 2: 'Approved', 3: 'Rejected', 4: 'Paid' };
        return map[status] ?? 'Unknown';
      },
      parseExpenseStatusColor(status) {
        const map = { '-1': 'error', 0: 'grey', 1: 'info', 2: 'primary', 3: 'warning', 4: 'success' };
        return map[status] ?? 'grey';
      },
    },
  };
</script>
